import type { ScreenDataMap } from '../types/screenData';
import type { VerificationResult } from '../types/verification';
import type { VerificationSessionState } from './SessionStateManager';
import { containsSensitiveBrowserText } from './browserSafety';

export type PublicVerificationStatus = 'verified' | 'failed' | 'cancelled' | 'error';

/**
 * L1 facial age estimation outcome. `unavailable` is a technical failure with
 * no age decision; it is never a below-threshold result.
 */
export type AgeEstimationStatus = 'estimated' | 'unavailable' | 'skipped';
const AGE_ESTIMATION_STATUSES = new Set<AgeEstimationStatus>([
  'estimated',
  'unavailable',
  'skipped',
]);

export type PublicFailureCategory =
  | 'verification_failed'
  | 'document_verification_failed'
  | 'face_match_failed'
  | 'face_match_unavailable'
  | 'liveness_failed'
  | 'expired_document'
  | 'cancelled';

type BrowserSafeVerificationOptions = {
  fallbackSessionId?: string;
  fallbackFailureCategory?: PublicFailureCategory;
};

export interface BrowserSafeVerificationResult {
  type: 'verification_result';
  success: boolean;
  status: PublicVerificationStatus;
  outcome: PublicVerificationStatus;
  failureCategory?: PublicFailureCategory;
  session_id?: string;
  sessionId?: string;
  timestamp?: number;
  ageVerified?: boolean;
  accessGranted?: boolean;
  requires_id_verification?: boolean;
  passes_age_challenge?: boolean;
  age_estimation_status?: AgeEstimationStatus;
  challenge_age?: number;
  age_estimate?: number;
  estimatedAge?: number;
  selfie_stored?: boolean;
  test_mode?: boolean;
  mode?: 'L1' | 'L2';
  liveness_result: boolean;
  error?: PublicFailureCategory | 'session_expired' | 'invalid_session';
  message?: PublicFailureCategory;
  retryable?: boolean;
  retryTarget?: 'document_capture';
}

type BrowserSafeDocumentVerificationResponse = {
  success: boolean;
  data: {
    verification_result: 'verified' | 'failed' | 'error';
    age_verified: boolean;
    session_id: string;
  };
  verification_result: 'verified' | 'failed' | 'error';
  session_id: string;
  age_verified: boolean;
  status: PublicVerificationStatus;
  outcome: PublicVerificationStatus;
  failureCategory?: PublicFailureCategory;
  error?: PublicFailureCategory;
  retryable?: boolean;
  retryTarget?: 'document_capture';
};

const PUBLIC_ERROR_CODES = new Set(['session_expired', 'invalid_session']);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const stringValue = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

const booleanValue = (value: unknown): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined;

const numberValue = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const nestedData = (source: Record<string, unknown>): Record<string, unknown> =>
  isRecord(source.data) ? source.data : {};

const normalizeStatus = (
  rawStatus: string | undefined,
  success: boolean
): PublicVerificationStatus => {
  if (rawStatus === 'verified' || rawStatus === 'VERIFIED') {
    return 'verified';
  }

  if (rawStatus === 'cancelled' || rawStatus === 'CANCELLED') {
    return 'cancelled';
  }

  if (rawStatus === 'error' || rawStatus === 'ERROR') {
    return 'error';
  }

  return success ? 'verified' : 'failed';
};

const publicFailureCategory = (
  source: Record<string, unknown>,
  options: BrowserSafeVerificationOptions
): PublicFailureCategory => {
  const rawCategory = stringValue(source.failureCategory);
  const rawError = stringValue(source.error);

  if (rawCategory === 'expired_document' || source.is_expired === true) {
    return 'expired_document';
  }

  if (rawCategory === 'cancelled') {
    return 'cancelled';
  }

  if (rawCategory === 'liveness_failed') {
    return 'liveness_failed';
  }

  // No usable face decision is a temporary problem, not a mismatch.
  if (rawCategory === 'face_match_unavailable') {
    return 'face_match_unavailable';
  }

  if (rawCategory === 'accepted_document_unavailable') {
    return 'document_verification_failed';
  }

  if (
    rawCategory === 'face_match_failed' ||
    rawCategory === 'selfie_reused' ||
    rawCategory === 'identity_mismatch' ||
    rawCategory === 'technical_face_match_failure' ||
    rawCategory === 'selfie_capture_quality' ||
    rawError?.toLowerCase().includes('face')
  ) {
    return 'face_match_failed';
  }

  if (
    rawCategory === 'document_verification_failed' ||
    rawCategory === 'document_capture_quality' ||
    stringValue(source.retryTarget) === 'document_capture'
  ) {
    return 'document_verification_failed';
  }

  // Honor an explicit generic engine failure. The caller-supplied fallback is
  // only meant to fill in a category when the engine did not classify the
  // failure; when the engine deliberately reports `verification_failed` (e.g.
  // the exception path in verify_document), preserve it instead of rewriting a
  // backend error as a document failure.
  if (rawCategory === 'verification_failed') {
    return 'verification_failed';
  }

  return options.fallbackFailureCategory || 'verification_failed';
};

const resolveSuccess = (source: Record<string, unknown>): boolean => {
  const explicitSuccess = booleanValue(source.success);
  if (explicitSuccess !== undefined) {
    return explicitSuccess;
  }

  const match = booleanValue(source.match);
  return match === true;
};

const resolveSessionId = (
  source: Record<string, unknown>,
  data: Record<string, unknown>,
  fallbackSessionId?: string
): string | undefined =>
  stringValue(source.session_id) ||
  stringValue(source.sessionId) ||
  stringValue(data.session_id) ||
  stringValue(data.sessionId) ||
  fallbackSessionId;

// A successful face result that still requires ID has not verified age.
const resolveAgeVerified = (
  source: Record<string, unknown>,
  data: Record<string, unknown>,
  success: boolean
): boolean | undefined =>
  booleanValue(source.ageVerified) ??
  booleanValue(source.age_verified) ??
  booleanValue(data.age_verified) ??
  (success && source.requires_id_verification !== true ? true : undefined);

const publicErrorCode = (
  source: Record<string, unknown>,
  failureCategory: PublicFailureCategory
): PublicFailureCategory | 'session_expired' | 'invalid_session' => {
  const rawError = stringValue(source.error);
  if (rawError && PUBLIC_ERROR_CODES.has(rawError)) {
    return rawError as 'session_expired' | 'invalid_session';
  }

  return failureCategory;
};

export function sanitizeVerificationResultForClient(
  raw: unknown,
  options: BrowserSafeVerificationOptions = {}
): VerificationResult {
  const source = isRecord(raw) ? raw : {};
  const data = nestedData(source);
  const success = resolveSuccess(source);
  const status = normalizeStatus(
    stringValue(source.status) ||
      stringValue(source.outcome) ||
      stringValue(source.verification_result) ||
      stringValue(data.verification_result),
    success
  );
  const sessionId = resolveSessionId(source, data, options.fallbackSessionId);
  const ageVerified = resolveAgeVerified(source, data, success);
  const failureCategory = success
    ? undefined
    : publicFailureCategory(source, options);

  const result: BrowserSafeVerificationResult = {
    type: 'verification_result',
    success,
    status,
    outcome: status,
    liveness_result: booleanValue(source.liveness_result) ?? success,
  };

  if (sessionId) {
    result.session_id = sessionId;
    result.sessionId = sessionId;
  }

  const timestamp = numberValue(source.timestamp);
  result.timestamp = timestamp ?? Date.now();

  if (ageVerified !== undefined) {
    result.ageVerified = ageVerified;
    result.accessGranted = ageVerified;
  }
  const passesAgeChallenge =
    booleanValue(source.passes_age_challenge) ?? ageVerified;
  if (passesAgeChallenge !== undefined) {
    result.passes_age_challenge = passesAgeChallenge;
  }

  const ageEstimationStatus = stringValue(source.age_estimation_status);
  if (
    ageEstimationStatus &&
    AGE_ESTIMATION_STATUSES.has(ageEstimationStatus as AgeEstimationStatus)
  ) {
    result.age_estimation_status = ageEstimationStatus as AgeEstimationStatus;
  }

  const challengeAge = numberValue(source.challenge_age);
  if (challengeAge !== undefined) {
    result.challenge_age = challengeAge;
  }

  const ageEstimate =
    numberValue(source.age_estimate) ?? numberValue(source.estimatedAge);
  if (ageEstimate !== undefined) {
    result.age_estimate = ageEstimate;
    result.estimatedAge = ageEstimate;
  }

  const requiresId = booleanValue(source.requires_id_verification);
  if (requiresId !== undefined) {
    result.requires_id_verification = requiresId;
  }

  const selfieStored = booleanValue(source.selfie_stored);
  if (selfieStored !== undefined) {
    result.selfie_stored = selfieStored;
  }

  const testMode = booleanValue(source.test_mode);
  if (testMode !== undefined) {
    result.test_mode = testMode;
  }

  if (source.mode === 'L1' || source.mode === 'L2') {
    result.mode = source.mode;
  }

  if (failureCategory) {
    result.failureCategory = failureCategory;
    result.error = publicErrorCode(source, failureCategory);
    result.message = failureCategory;
  }

  if (
    source.retryable === true &&
    stringValue(source.retryTarget) === 'document_capture'
  ) {
    result.retryable = true;
    result.retryTarget = 'document_capture';
  }

  return result as VerificationResult;
}

export function sanitizeVerificationResponseForClient(
  raw: unknown,
  options: BrowserSafeVerificationOptions = {}
): BrowserSafeDocumentVerificationResponse {
  const safeResult = sanitizeVerificationResultForClient(raw, options);
  const sessionId = safeResult.session_id || options.fallbackSessionId || '';
  const verificationResult =
    safeResult.status === 'error'
      ? 'error'
      : safeResult.success
        ? 'verified'
        : 'failed';
  const status = safeResult.status || (safeResult.success ? 'verified' : 'failed');
  const failureCategory = safeResult.failureCategory as
    | PublicFailureCategory
    | undefined;
  const retryTarget =
    safeResult.retryTarget === 'document_capture' ? 'document_capture' : undefined;

  return {
    success: safeResult.success,
    data: {
      verification_result: verificationResult,
      age_verified: safeResult.ageVerified === true,
      session_id: sessionId,
    },
    verification_result: verificationResult,
    session_id: sessionId,
    age_verified: safeResult.ageVerified === true,
    status,
    outcome: safeResult.outcome || status,
    ...(failureCategory && {
      failureCategory,
      error: failureCategory,
    }),
    ...(safeResult.retryable && retryTarget && {
      retryable: true,
      retryTarget,
    }),
  };
}

const sanitizeErrorMessage = (message: unknown): string | undefined => {
  if (typeof message !== 'string') {
    return undefined;
  }

  if (containsSensitiveBrowserText(message)) {
    return 'verification_failed';
  }

  if (PUBLIC_ERROR_CODES.has(message)) {
    return message;
  }

  if (
    message === 'verification_failed' ||
    message === 'document_verification_failed' ||
    message === 'face_match_failed' ||
    message === 'liveness_failed' ||
    message === 'expired_document' ||
    message === 'cancelled'
  ) {
    return message;
  }

  return undefined;
};

export function sanitizeScreenDataForClient<T extends ScreenDataMap[keyof ScreenDataMap]>(
  data: T
): T {
  if (!isRecord(data)) {
    return data;
  }

  const safeData = { ...data } as Record<string, unknown>;
  if ('verificationResult' in safeData && safeData.verificationResult) {
    safeData.verificationResult = sanitizeVerificationResultForClient(
      safeData.verificationResult
    );
  }

  const safeErrorMessage = sanitizeErrorMessage(safeData.errorMessage);
  if ('errorMessage' in safeData) {
    if (safeErrorMessage) {
      safeData.errorMessage = safeErrorMessage;
    } else {
      delete safeData.errorMessage;
    }
  }

  return safeData as T;
}

export function sanitizeScreenDataRecordForClient(
  screenData: VerificationSessionState['screenFlow']['screenData']
): VerificationSessionState['screenFlow']['screenData'] {
  const safeRecord: VerificationSessionState['screenFlow']['screenData'] = {};

  for (const [screen, data] of Object.entries(screenData)) {
    if (data) {
      safeRecord[screen as keyof ScreenDataMap] = sanitizeScreenDataForClient(
        data as ScreenDataMap[keyof ScreenDataMap]
      );
    }
  }

  return safeRecord;
}

export function sanitizeVerificationSessionStateForClient<
  T extends Partial<VerificationSessionState>,
>(state: T): T {
  const safeState = { ...state };

  if (safeState.config) {
    const safeConfig = {
      ...safeState.config,
    } as VerificationSessionState['config'] & {
      identityVerificationEnabled?: boolean;
    };

    if ('faceMatchEnabled' in safeConfig) {
      safeConfig.identityVerificationEnabled = safeConfig.faceMatchEnabled;
      delete safeConfig.faceMatchEnabled;
    }

    safeState.config = safeConfig;
  }

  if (safeState.screenFlow?.screenData) {
    safeState.screenFlow = {
      ...safeState.screenFlow,
      screenData: sanitizeScreenDataRecordForClient(
        safeState.screenFlow.screenData
      ),
    };
  }

  if (safeState.verification?.verificationResult) {
    safeState.verification = {
      ...safeState.verification,
      verificationResult: sanitizeVerificationResultForClient(
        safeState.verification.verificationResult
      ),
    };
  }

  if (safeState.verification?.captureProgress) {
    const safeCaptureProgress = {
      ...(safeState.verification.captureProgress as unknown as Record<
        string,
        unknown
      >),
    };
    delete safeCaptureProgress.qualityScores;
    safeState.verification = {
      ...safeState.verification,
      captureProgress:
        safeCaptureProgress as unknown as VerificationSessionState['verification']['captureProgress'],
    };
  }

  return safeState;
}
